import { playerOrderCollision } from "@/features/scouting/utils/playerOrderCollision";
/** Pointer, touch and keyboard drag boundary shared by Table and Pitch. */
import {
  DndContext,
  DragOverlay,
  KeyboardSensor,
  MouseSensor,
  TouchSensor,
  type DragEndEvent,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import React, { memo, useCallback, useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { Platform } from "react-native";
import TableDragPreview from "./WatchlistOrderDndContextView";
import {
  createTableDragPreview,
  highlightDroppedRow,
} from "@/features/scouting/utils/watchlistTableDragPreview";

import { Stack } from "@/components/atoms/Stack";
import { Text } from "@/components/atoms/Text";

/**
 * Renders a drag overlay at <body> on web, where `position: fixed` means the
 * viewport again; native has no DOM and keeps it in place.
 * @param overlay - The DragOverlay element
 * @returns The overlay, portalled on web.
 */
export const portalToBody = (overlay: React.ReactElement): React.ReactNode =>
  // Capability gate (DOM portals exist only on web), not a layout decision.
  Platform.OS === "web" && typeof document !== "undefined"
    ? createPortal(overlay, document.body)
    : overlay;

/** Props for the shared watchlist drag boundary. */
interface WatchlistOrderDndContextProps {
  /** Table rows move as complete sortable elements rather than a text overlay. */
  readonly moveTableRow?: boolean;
  readonly playerIds: readonly string[];
  readonly playerNames: Readonly<Record<string, string>>;
  readonly disabled?: boolean;
  readonly onReorder: (orderedPlayerIds: readonly string[]) => void;
  readonly children: React.ReactNode;
}

/**
 * Provides equivalent pointer, long-press touch and keyboard movement.
 * @param root0 - Rendered subset and its reorder callback.
 * @param root0.moveTableRow - Move the actual table row instead of a text overlay.
 * @param root0.playerIds - Visible player identifiers in current order.
 * @param root0.playerNames - Display names keyed by player identifier.
 * @param root0.disabled - Whether drag interactions are disabled.
 * @param root0.onReorder - Receives the reordered visible player identifiers.
 * @param root0.children - Sortable rows or cards inside the boundary.
 */
const WatchlistOrderDndContext = ({
  moveTableRow = false,
  playerIds,
  playerNames,
  disabled = false,
  onReorder,
  children,
}: WatchlistOrderDndContextProps) => {
  const [activeId, setActiveId] = useState<string | null>(null);
  const [activeRow, setActiveRow] = useState<HTMLElement | null>(null);
  const [droppedRow, setDroppedRow] = useState<HTMLElement | null>(null);
  const attachPreview = useCallback(
    (host: HTMLElement | null) => {
      if (host && activeRow)
        host.replaceChildren(createTableDragPreview(activeRow));
    },
    [activeRow]
  );
  const sensors = useSensors(
    // Keep mouse and touch sensors disjoint. PointerSensor also receives touch
    // pointer events and races the delayed TouchSensor on real long presses.
    useSensor(MouseSensor, { activationConstraint: { distance: 8 } }),
    useSensor(TouchSensor, {
      activationConstraint: { delay: 250, tolerance: 5 },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );
  const handleDragStart = useCallback(({ active }: DragEndEvent) => {
    setActiveId(String(active.id));
    setActiveRow(active.data?.current?.elementRef?.current ?? null);
    setDroppedRow(null);
  }, []);
  const handleDragCancel = useCallback(() => {
    setActiveId(null);
    setActiveRow(null);
  }, []);
  const handleDragEnd = useCallback(
    ({ active, over }: DragEndEvent) => {
      setActiveId(null);
      setActiveRow(null);
      if (disabled || !over || active.id === over.id) return;
      const ids: readonly string[] =
        active.data?.current?.groupIds ?? playerIds;
      const from = ids.indexOf(String(active.id));
      const to = ids.indexOf(String(over.id));
      if (from < 0 || to < 0) return;
      if (moveTableRow)
        setDroppedRow(active.data?.current?.elementRef?.current ?? null);
      onReorder(arrayMove([...ids], from, to));
    },
    [disabled, onReorder, playerIds, moveTableRow]
  );
  const playerName = useCallback(
    (playerId: string | number) =>
      playerNames[String(playerId)] ?? String(playerId),
    [playerNames]
  );
  useEffect(() => {
    if (!droppedRow) return;
    const clear = highlightDroppedRow(droppedRow);
    const timer = setTimeout(clear, 600);
    return () => {
      clearTimeout(timer);
      clear();
    };
  }, [droppedRow]);

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={playerOrderCollision}
      onDragEnd={handleDragEnd}
      onDragStart={handleDragStart}
      onDragCancel={handleDragCancel}
      accessibility={{
        screenReaderInstructions: {
          draggable:
            "Press space to lift. Use arrow keys to move, space to drop, or escape to cancel.",
        },
        announcements: {
          onDragStart: ({ active }) =>
            `Lifted player ${playerName(active.id)}.`,
          onDragOver: ({ active, over }) =>
            over
              ? `Player ${playerName(active.id)} moved over ${playerName(over.id)}.`
              : `Player ${playerName(active.id)} is no longer over a position.`,
          onDragEnd: ({ active, over }) =>
            over
              ? `Player ${playerName(active.id)} dropped at ${playerName(over.id)}.`
              : `Move cancelled for player ${playerName(active.id)}.`,
          onDragCancel: ({ active }) =>
            `Move cancelled for player ${playerName(active.id)}.`,
        },
      }}
    >
      <SortableContext
        items={[...playerIds]}
        strategy={verticalListSortingStrategy}
      >
        {children}
      </SortableContext>
      {moveTableRow && typeof document !== "undefined"
        ? createPortal(
            <DragOverlay dropAnimation={null}>
              {activeRow ? <TableDragPreview hostRef={attachPreview} /> : null}
            </DragOverlay>,
            document.body
          )
        : // SE-8526: portalled to <body> on web like the table's preview. The
          // overlay is position: fixed, and inside the Pitch's scrolling side
          // panel a transformed ancestor re-anchors it — the preview then
          // floated far from the pointer.
          portalToBody(
            <DragOverlay>
              {activeId ? (
                <Stack
                  testID="watchlist-order-drag-overlay"
                  direction="horizontal"
                  className="items-center rounded-sm border border-outline-default bg-surface-base px-3 py-2 shadow-sm"
                >
                  <Text variant="micro" className="text-content-primary">
                    Moving player
                  </Text>
                </Stack>
              ) : null}
            </DragOverlay>
          )}
    </DndContext>
  );
};

export default memo(WatchlistOrderDndContext);
